import { useEffect } from 'react';
import { isDesktopRuntime } from './desktopDetection';
import { reportDesktopBusy } from './desktopRuntimeLazy';
import type { DesktopBusyReasonType } from './desktopRuntime';

/**
 * What this window has in progress, for the desktop's idle install (F-0545).
 *
 * An opted-in app installs a staged update once the machine has gone unused for the channel's wait —
 * but only if nothing would be lost by the restart. The window is the one that knows about drafts,
 * dialogs and uploads, so it reports them; the app adds a watched terminal and its own download.
 *
 * - **A draft** is a multi-line field or rich editor the person has typed into that still holds text.
 *   Typing is what makes it a draft: an editor showing saved text nobody touched is not one, and a
 *   single-line field such as a search box never is.
 * - **A dialog** is any open modal (`aria-modal`, which `ModalFrame` and `Sheet` set), an alert
 *   dialog, or the system's sign-in sheet while it is up.
 * - **A transfer** is an upload in flight.
 *
 * The sheet and uploads are counted by the code that starts them ({@link whileBusy}).
 */

/** How often the window looks. A handful of DOM queries; the report is sent only when it changes. */
const BUSY_PROBE_INTERVAL_MS = 2000;

const DRAFT_FIELDS = 'textarea, [contenteditable="true"], [contenteditable=""]';
const OPEN_DIALOGS = '[aria-modal="true"], [role="alertdialog"]';

const inFlight: Record<'transfer' | 'dialog', number> = { transfer: 0, dialog: 0 };

/** Count `work` as in progress for as long as it runs. */
export async function whileBusy<T>(reason: 'transfer' | 'dialog', work: Promise<T>): Promise<T> {
  inFlight[reason] += 1;
  try {
    return await work;
  } finally {
    inFlight[reason] -= 1;
  }
}

/** The fields the person has typed into, so far. */
export function createDraftTracker(root: Document = document) {
  const edited = new WeakSet<Element>();
  const onInput = (event: Event) => {
    const field = event.target instanceof Element ? event.target.closest(DRAFT_FIELDS) : null;
    if (field) edited.add(field);
  };
  root.addEventListener('input', onInput, true);
  return {
    edited,
    stop: () => root.removeEventListener('input', onInput, true),
  };
}

function holdsText(field: Element): boolean {
  if (field instanceof HTMLTextAreaElement) {
    return !field.readOnly && !field.disabled && field.value.trim() !== '';
  }
  return (field.textContent ?? '').trim() !== '';
}

/** What `root` has in progress now, each reason once. */
export function busyReasons(root: Document, edited: WeakSet<Element>): DesktopBusyReasonType[] {
  const reasons: DesktopBusyReasonType[] = [];
  if (inFlight.dialog > 0 || root.querySelector(OPEN_DIALOGS)) reasons.push('dialog');
  const drafted = Array.from(root.querySelectorAll(DRAFT_FIELDS)).some(
    (field) => edited.has(field) && holdsText(field),
  );
  if (drafted) reasons.push('draft');
  if (inFlight.transfer > 0) reasons.push('transfer');
  return reasons;
}

/**
 * Keep the desktop told what this window has in progress. Mounted once per window, by the update
 * control; nothing on the web.
 */
export function useDesktopBusyReporter(): void {
  useEffect(() => {
    if (!isDesktopRuntime()) return undefined;
    const drafts = createDraftTracker(document);
    let sent = '[]';
    const send = (reasons: DesktopBusyReasonType[]) => {
      const next = JSON.stringify(reasons);
      if (next === sent) return;
      sent = next;
      // A build from before F-0545 has no such command, and has no idle install to inform.
      reportDesktopBusy(reasons).catch(() => undefined);
    };
    const timer = window.setInterval(
      () => send(busyReasons(document, drafts.edited)),
      BUSY_PROBE_INTERVAL_MS,
    );
    return () => {
      window.clearInterval(timer);
      drafts.stop();
      send([]);
    };
  }, []);
}
